import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Box, Link, Typography, useMediaQuery, useTheme} from '@mui/material';
import {keyframes} from '@mui/system';
import useAuth from './Auth';

const APPEAR_TIMEOUT = 2000;
const VISIBLE_DURATION = 6000;
const TRANSITION_DURATION = 400;
const CYCLE_DURATION = VISIBLE_DURATION + 2 * TRANSITION_DURATION;

const BASE_HINTS: React.ReactNode[] = [
	'Hitting Spacebar is a quick way to add or edit (selected) items.',
	'Use Tab, Enter and Esc keys to quickly navigate through your hierarchy.',
	'Use Backspace to delete a highlighted item.',
	'Hold Shift to draw a box and select multiple items at once.',
];

const slideCycle = keyframes`
  0% {
    transform: translateX(100%);
    opacity: 0;
  }
  ${((TRANSITION_DURATION / CYCLE_DURATION) * 100).toFixed(2)}% {
    transform: translateX(0);
    opacity: 1;
  }
  ${(
		((TRANSITION_DURATION + VISIBLE_DURATION) / CYCLE_DURATION) *
   100
	).toFixed(2)}% {
    transform: translateX(0);
    opacity: 1;
  }
  100% {
    transform: translateX(-100%);
    opacity: 0;
  }
`;

const HintsBanner = () => {
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
	const [isActive, setIsActive] = useState<boolean>(false);
	const [hintIndex, setHintIndex] = useState<number>(0);
	const {isAuthenticated, isAuthenticating, loginWithRedirect} = useAuth();
	const appearTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const cycleIntervalRef = useRef<NodeJS.Timeout | null>(null);

	const hints = useMemo<React.ReactNode[]>(() => {
		const list: React.ReactNode[] = [];

		if (isDesktop) {
			list.push(...BASE_HINTS);
		}

		if (!isAuthenticating && !isAuthenticated) {
			list.push(
				<>
					You&apos;re in offline mode. Create a user account&nbsp;
					<Link
						tabIndex={-1}
						href="#login"
						onClick={() => loginWithRedirect()}
						sx={{
							textDecoration: 'underline',
							fontSize: '14px',
						}}
					>
						here
					</Link>
					&nbsp;to access your work on any device.
				</>,
			);
		}
		return list;
	}, [isAuthenticated, isAuthenticating, isDesktop, loginWithRedirect]);

	useEffect(() => {
		appearTimeoutRef.current = setTimeout(() => {
			if (hints.length === 0) {
				return;
			}

			setIsActive(true);

			if (hints.length > 1) {
				cycleIntervalRef.current = setInterval(() => {
					setHintIndex(prev => prev + 1);
				}, CYCLE_DURATION);
			}
		}, APPEAR_TIMEOUT);

		return () => {
			if (appearTimeoutRef.current) {
				clearTimeout(appearTimeoutRef.current);
			}
			if (cycleIntervalRef.current) {
				clearInterval(cycleIntervalRef.current);
			}
		};
	}, [hints.length]);

	if (hints.length === 0) {
		return null;
	}

	const currentHint = hints[hintIndex % hints.length];

	if (!isActive) {
		return null;
	}

	return (
		<Box
			sx={{
				px: 2,
				py: 0.5,
				position: 'fixed',
				bottom: 0,
				left: 0,
				width: '100%',
				color: 'text.secondary',
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				gap: 1,
				overflow: 'hidden',
			}}
		>
			<Box
				key={hintIndex}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					animation: hints.length > 1 ? `${CYCLE_DURATION}ms ${slideCycle} ease-in-out` : 'none',
					userSelect: 'none',
				}}
			>
				<Typography component="span" variant="body2" fontStyle="italic">
					{currentHint}
				</Typography>
			</Box>
		</Box>
	);
};

export default HintsBanner;

